import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./LearningPathDetail.css";
import { apiFetch } from '../../utils/api';

const LearningPathDetail = () => {
  const { learning_path_id } = useParams();
  const navigate = useNavigate();
  const [learningPath, setLearningPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollingCourses, setEnrollingCourses] = useState({}); // Track enrolling state per course

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    apiFetch(`${window._env_.API_BASE_URL}/user_learning_paths/${learning_path_id}?user_email=${encodeURIComponent(userEmail)}`)
      .then((data) => {
        setLearningPath(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching learning path details:", error);
        setError("Failed to load learning path details. Please try again later.");
        setLoading(false);
      });
  }, [learning_path_id]);

  const handleMyLearningPathsClick = () => {
    navigate(-1); // Redirect to previous page (same as back button)
  };

  const handleEnroll = async (courseId, courseName) => {
    setEnrollingCourses((prev) => ({ ...prev, [courseId]: true })); // Set enrolling state
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
        navigate('/Login');
        setEnrollingCourses((prev) => ({ ...prev, [courseId]: false }));
        return;
      }

      const userInfo = await apiFetch(`${window._env_.API_BASE_URL}/users/info?email=${userEmail}`);
      const userId = userInfo.user_id;

      const enrollResponse = await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId }),
      });

      const snackbar = document.createElement('div');
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';

      if (enrollResponse.detail && enrollResponse.detail.includes('already enrolled')) {
        snackbar.textContent = 'You are already enrolled in this course.';
        snackbar.style.backgroundColor = '#f44336';
      } else {
        snackbar.textContent = 'Successfully enrolled, redirecting...';
        snackbar.style.backgroundColor = '#4CAF50';
      }

      document.body.appendChild(snackbar);
      setTimeout(() => {
        snackbar.remove();
        if (!enrollResponse.detail?.includes('already enrolled')) {
          navigate('/course-videos', { state: { department: learningPath.department || 'default-department', courseId } });
        }
        setEnrollingCourses((prev) => ({ ...prev, [courseId]: false }));
      }, 3000);
    } catch (error) {
      console.error('Enrollment error:', error);
      const snackbar = document.createElement('div');
      snackbar.textContent = 'An error occurred during enrollment. Please try again.';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background-color: #f44336; color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';
      document.body.appendChild(snackbar);
      setTimeout(() => {
        snackbar.remove();
        setEnrollingCourses((prev) => ({ ...prev, [courseId]: false }));
      }, 3000);
    }
  };

  const handleLearnNow = (courseId, departmentName) => {
    navigate('/course-videos', { state: { department: departmentName || 'default-department', courseId } });
  };

  const handleLinkClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="lpd-loading">Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!learningPath) {
    return <div>No learning path data available.</div>;
  }

  return (
    <div className="lpd-container">
      {/* Breadcrumb Navigation */}
      <div className="lpd-breadcrumb">
        <span
          className="lpd-breadcrumb-link"
          onClick={handleMyLearningPathsClick}
          style={{ cursor: 'pointer', color: '#007bff' }}
        >
          My Learning Paths
        </span>
        <span className="lpd-breadcrumb-divider"> &gt; </span>
        <span>{learningPath.name}</span>
      </div>

      {/* Stats Grid */}
      <div className="lpd-stats-grid">
        <div className="lpd-stat-card">
          <div className="lpd-stat-number">{learningPath.total_credits}</div>
          <div className="lpd-stat-label">Total Credits</div>
        </div>
        <div className="lpd-stat-card">
          <div className="lpd-stat-number">{learningPath.total_courses}</div>
          <div className="lpd-stat-label">Total Courses</div>
        </div>
        <div className="lpd-stat-card">
          <div className="lpd-stat-number">{(learningPath.total_duration_hours || 0).toFixed(2)}</div>
          <div className="lpd-stat-label">Total Hours</div>
        </div>
        <div className="lpd-stat-card lpd-highlight-blue">
          <div className="lpd-stat-number">{learningPath.overall_progress}%</div>
          <div className="lpd-stat-label">Overall Progress</div>
        </div>
        <div className="lpd-stat-card lpd-highlight-green">
          <div className="lpd-stat-number">{learningPath.credits_earned}</div>
          <div className="lpd-stat-label">Credits Earned</div>
        </div>
      </div>

      {/* Table Container */}
      <div className="lpd-table-container">
        <h2 className="lpd-table-title">Courses in this Learning Path</h2>

        <div className="lpd-table-wrapper">
          <table className="lpd-table">
            <thead>
              <tr>
                <th>Course Name</th>
                <th>Credits</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Duration</th>
                <th>Videos</th>
                <th>Mandatory</th>
                <th>Progress</th>
                <th>Action</th>
                <th>Status</th>
                <th>Certified</th>
              </tr>
            </thead>
            <tbody>
              {learningPath.courses.map((course, index) => (
                <tr key={index}>
                  <td className="lpd-course-name">
                    {course.name}{course.is_external_course === "yes" ? (
                      <span className="lpd-external-label"> (External)</span>
                    ) : ""}
                  </td>
                  <td>{course.credits}</td>
                  <td>{course.start_date || "N/A"}</td>
                  <td>{course.end_date || "N/A"}</td>
                  <td>{course.duration}</td>
                  <td>{course.videos}</td>
                  <td>{course.is_mandatory ? "Yes" : "No"}</td>
                  <td className="lpd-progress-column">
                    <div className="lpd-progress-container">
                      <div
                        className="lpd-progress-bar"
                        style={{ width: `${course.progress}%` }}
                      ></div>
                      <span className="lpd-progress-text">{course.progress}%</span>
                    </div>
                  </td>
                  <td>
                    {course.is_external_course === "yes" ? (
                      <button
                        className="lpd-btn-enroll"
                        onClick={() => handleLinkClick(course.enrollment_url)}
                        disabled={!course.enrollment_url}
                      >
                        Link
                      </button>
                    ) : course.enrolled ? (
                      <button
                        className="lpd-btn-enroll"
                        onClick={() => handleLearnNow(course.course_id, learningPath.department)}
                      >
                        Learn Now
                      </button>
                    ) : (
                      <button
                        className="lpd-btn-enroll"
                        onClick={() => handleEnroll(course.course_id, course.name)}
                        disabled={enrollingCourses[course.course_id]}
                      >
                        {enrollingCourses[course.course_id] ? 'Enrolling...' : 'Enroll Now'}
                      </button>
                    )}
                  </td>
                  <td>
                    {course.status === "Completed" ? (
                      <span className="lpd-status-completed">Completed</span>
                    ) : (
                      <span className="lpd-status-pending">Not Completed</span>
                    )}
                  </td>
                  <td>
                    {course.certified === "yes" ? (
                      <span className="lpd-status-certified">Yes</span>
                    ) : (
                      <span className="lpd-status-pending">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LearningPathDetail;